import React, { useState, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { Helmet } from "react-helmet"
import Icon from "@mdi/react"
import {
  mdiCar,
  mdiCheckCircle,
  mdiMapMarkerPath,
  mdiClockOutline,
  mdiAirplane,
  mdiShieldCheck,
} from "@mdi/js"
import { slugify } from "../../../utils/slugify"
import { getPopularCitiesInState } from "../../../utils/cityStateMap"
import Hatchback from "../../../assets/cars/hatchback.webp"
import Sedan from "../../../assets/cars/sedan.webp"
import SUV from "../../../assets/cars/suv.webp"
import demo from "../../../assets/cities/temp.png"
import "./cityPairView.scss"

const ICON_MAP = {
  mdiCar,
  mdiAirplane,
  mdiMapMarkerPath,
  mdiClockOutline,
  mdiCheckCircle,
}

const VEHICLE_IMAGE_MAP = { Hatchback, Sedan, SUV }

const CityPairView = ({
  fromCity,
  toCity,
  fromCityName,
  toCityName,
  fromCityData,
  toCityData,
  loading,
}) => {
  const navigate = useNavigate()
  const { distance } = useSelector((state) => state.cab)
  const [openFaqIndex, setOpenFaqIndex] = useState(null)

  // Get popular cities in the same state as fromCity
  const popularCitiesInState = useMemo(() => {
    return getPopularCitiesInState(fromCityName, fromCityName, 21)
  }, [fromCityName])

  // Extract data with fallbacks
  const fromSections = fromCityData?.sections || {}
  const toSections = toCityData?.sections || {}

  // Hero section data
  const heroImage = fromCityData?.hero?.imageUrl || toCityData?.hero?.imageUrl || demo
  const heroTitle = `${fromCityName} to ${toCityName} Cab Service`

  // Intro section - combine from both cities
  const introHeading = `Why Book ${fromCityName} to ${toCityName} Cab with Pulpit?`
  const introParagraphs = useMemo(() => {
    const fromIntro = fromSections.intro?.paragraphs?.[0] || ""
    const toIntro = toSections.intro?.paragraphs?.[0] || ""
    return [
      `${fromCityName} to ${toCityName} is a popular outstation route. When you book a one-way cab from ${fromCityName} to ${toCityName}, you expect punctual service, clean vehicles, and drivers familiar with the route. Our ${fromCityName} to ${toCityName} cab service focuses on reliability, easy booking, and transparent pricing.`,
      fromIntro || `Whether you need a one-way cab from ${fromCityName} to ${toCityName} for business travel, family trips, or special occasions, we aim to keep every journey comfortable and safe.`,
      toIntro || `Planning a trip from ${fromCityName} to ${toCityName}? Book your one-way outstation cab now with transparent pricing and verified drivers.`,
    ]
  }, [fromCityName, toCityName, fromSections, toSections])

  // Taxi rates
  const taxiRates = fromSections.taxiRates?.rates || []

  // Popular routes
  const popularRoutes = fromSections.popularRoutes?.routes || []

  // Services - use fromCity services
  const services = fromSections.services?.items || []

  // Local areas
  const fromAreas = fromSections.localAreas?.areas || []
  const fromInfoCards = fromSections.localAreas?.infoCards || []
  const toAreas = toSections.localAreas?.areas || []
  const toInfoCards = toSections.localAreas?.infoCards || []

  // Travel Ideas - use fromCity travel ideas
  const travelIdeas = fromSections.travelIdeas?.cards || []

  // Why Choose
  const whyChooseFeatures = fromSections.whyChoose?.features || []

  // FAQ items
  const faqItems = useMemo(() => {
    const fromFaqs = fromSections.faq?.items || []
    const toFaqs = toSections.faq?.items || []
    const routeSpecificFaqs = [
      {
        q: `How do I book a cab from ${fromCityName} to ${toCityName}?`,
        a: `You can book a one-way outstation cab from ${fromCityName} to ${toCityName} by selecting your pickup and drop locations, date, and time on our booking page. Complete the booking with transparent pricing and verified drivers.`,
      },
      {
        q: `Is one-way cab service available from ${fromCityName} to ${toCityName}?`,
        a: `Yes, one-way cab service is available from ${fromCityName} to ${toCityName}. You only pay for the one-way journey with transparent pricing.`,
      },
      {
        q: `What is the distance and estimated time from ${fromCityName} to ${toCityName}?`,
        a: distance
          ? `The distance is approximately ${Math.ceil(distance)} km. Travel time will vary based on traffic and route conditions.`
          : `Distance and time will be calculated at the time of booking. Our drivers are familiar with the best routes between ${fromCityName} and ${toCityName}.`,
      },
      {
        q: `Are tolls and parking included in the fare for ${fromCityName} to ${toCityName} trip?`,
        a: `Tolls, parking, and state taxes may be extra depending on the route and package. Your final fare confirmation will include all charges clearly mentioned.`,
      },
    ]
    // Combine route-specific FAQs with city FAQs, avoiding duplicates
    const allFaqs = [...routeSpecificFaqs]
    fromFaqs.forEach((faq) => {
      if (!allFaqs.some((f) => f.q === faq.q)) {
        allFaqs.push(faq)
      }
    })
    toFaqs.forEach((faq) => {
      if (!allFaqs.some((f) => f.q === faq.q)) {
        allFaqs.push(faq)
      }
    })
    return allFaqs.slice(0, 8)
  }, [fromCityName, toCityName, distance, fromSections, toSections])

  // Convert route link text to slugified route
  const convertRouteToSlug = (linkText) => {
    if (!linkText) return fromSections?.popularRoutes?.table?.linkTo || "/bookCab"
    const match = linkText.match(/(.+?)\s+to\s+(.+)/i)
    if (match) {
      const from = slugify(match[1].trim())
      const to = slugify(match[2].trim())
      return `/routes/${from}-to-${to}-cab`
    }
    return fromSections?.popularRoutes?.table?.linkTo || "/bookCab"
  }

  // JSON-LD Schema
  const jsonLdSchema = useMemo(() => {
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://pulpitcabs.com"
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: `${baseUrl}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: `${fromCityName} Cab Service`,
              item: `${baseUrl}/cities/${slugify(fromCity)}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: `${fromCityName} to ${toCityName} Cab`,
              item: `${baseUrl}/routes/${slugify(fromCity)}-to-${slugify(toCity)}-cab`,
            },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: faqItems.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.a,
            },
          })),
        },
        {
          "@type": "Service",
          serviceType: "Outstation Cab Service",
          areaServed: [
            {
              "@type": "City",
              name: fromCityName,
            },
            {
              "@type": "City",
              name: toCityName,
            },
          ],

          name: heroTitle,
          provider: {
            "@type": "Organization",
            name: "Pulpit Cab",
          },
        },
      ],
    }
  }, [fromCity, toCity, fromCityName, toCityName, faqItems, heroTitle])

  if (loading) {
    return (
      <div className="city-view">
        <div className="city-view__container">
          <div style={{ padding: "4rem", textAlign: "center" }}>Loading route details...</div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLdSchema)}</script>
      </Helmet>
      <div className="city-view pt-32">
        {/* Hero Image Section */}
        <div className="city-view__hero relative">
          <img
            src={heroImage}
            alt={`${fromCityName} to ${toCityName} cab route`}
            className="city-view__hero-image w-full"
          />

          <div className="absolute inset-0 flex justify-center items-start bg-black/40 pt-10 md:pt-16">
            <h1 className="text-white text-4xl md:text-6xl font-bold text-center">
              {heroTitle}
            </h1>
          </div>
        </div>


        <div className="city-view__container">
          {/* Intro */}
          <section className="city-view__section">
            <h2 className="city-view__heading">{introHeading}</h2>
            {introParagraphs.map((p, idx) => (
              <p key={idx} className="city-view__paragraph">
                {p}
              </p>
            ))}
          </section>

          {/* Taxi Rates */}
          {taxiRates.length > 0 && (
            <section className="city-view__section city-view__section--highlighted">
              <h2 className="city-view__heading">
                {fromSections.taxiRates?.heading || `Cab Options & Rates for ${fromCityName} to ${toCityName}`}
              </h2>
              <p className="city-view__paragraph">{fromSections.taxiRates?.subText}</p>

              <div className="city-view__rates-grid">
                {taxiRates.map((rate, index) => (
                  <div
                    key={index}
                    className={`city-view__rate-card ${rate.highlight ? "city-view__rate-card--highlight" : ""
                      }`}
                  >
                    {rate.highlight && (
                      <span className="city-view__rate-badge-highlight">
                        {rate.highlightText || "Popular Choice"}
                      </span>
                    )}

                    <div className="h-44 flex items-center justify-center">
                      <img
                        src={VEHICLE_IMAGE_MAP[rate.type] || Sedan}
                        alt={rate.imageAlt || `${rate.type} cab`}
                        className="h-32 object-contain"
                      />
                    </div>

                    <h3 className="city-view__rate-type">{rate.type}</h3>
                    <p className="city-view__rate-model">{rate.desc}</p>
                    <div className="city-view__rate-badge">{rate.seats} seats</div>
                    <div className="city-view__rate-price">{rate.rate}</div>
                    <p className="city-view__rate-best">{rate.bestFor}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Popular Routes */}
          {popularRoutes.length > 0 && (
            <section className="city-view__section">
              <h2 className="city-view__heading">
                {fromSections.popularRoutes?.heading || `Popular Routes from ${fromCityName}`}
              </h2>
              <p className="city-view__paragraph">{fromSections.popularRoutes?.subText}</p>

              <div className="city-view__routes-table">
                <table className="city-view__table">
                  <thead>
                    <tr>
                      {(fromSections.popularRoutes?.table?.headers || [
                        "Route",
                        "Distance & Time",
                        "Starting Price",
                      ]).map((h, idx) => (
                        <th key={idx}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {popularRoutes.map((route, index) => {
                      const routeUrl = convertRouteToSlug(route.linkText)
                      return (
                        <tr key={index}>
                          <td>
                            <Link to={routeUrl} className="city-view__route-link">
                              {route.linkText}
                            </Link>
                          </td>
                          <td>{route.distanceAndTime}</td>
                          <td className="city-view__price">{route.price}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Services */}
          {services.length > 0 && (
            <section className="city-view__section city-view__section--services">
              <h2 className="city-view__heading">
                {fromSections.services?.heading || `Our Cab Services for ${fromCityName} to ${toCityName}`}
              </h2>

              <div className="city-view__services-grid">
                {services.map((service, index) => (
                  <div key={index} className="city-view__service-card">
                    <div className="city-view__service-icon">
                      <Icon
                        path={ICON_MAP[service.iconKey] || mdiCar}
                        size={4}
                        color="#21A5A0"
                      />
                    </div>
                    <h3 className="city-view__service-title text-center">{service.title}</h3>
                    <p className="city-view__service-description">{service.description}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Pickup Areas in FromCity */}
          {fromAreas.length > 0 && (
            <section className="city-view__section">
              <h2 className="city-view__heading">
                {fromSections.localAreas?.heading || `Pickup Areas in ${fromCityName}`}
              </h2>
              <p className="city-view__paragraph">{fromSections.localAreas?.subText}</p>

              <div className="city-view__local-areas">
                {fromAreas.map((area, index) => (
                  <span key={index} className="city-view__area-tag">
                    {area}
                  </span>
                ))}
              </div>

              {fromInfoCards.length > 0 && (
                <div className="city-view__local-info">
                  {fromInfoCards.map((card, idx) => (
                    <div key={idx} className="city-view__info-card">
                      <h3 className="city-view__info-title">{card.title}</h3>
                      <p className="city-view__info-text">{card.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Drop Areas in ToCity */}
          {toAreas.length > 0 && (
            <section className="city-view__section">
              <h2 className="city-view__heading">
                {toSections.localAreas?.heading || `Drop Areas in ${toCityName}`}
              </h2>
              <p className="city-view__paragraph">{toSections.localAreas?.subText}</p>

              <div className="city-view__local-areas">
                {toAreas.map((area, index) => (
                  <span key={index} className="city-view__area-tag">
                    {area}
                  </span>
                ))}
              </div>

              {toInfoCards.length > 0 && (
                <div className="city-view__local-info">
                  {toInfoCards.map((card, idx) => (
                    <div key={idx} className="city-view__info-card">
                      <h3 className="city-view__info-title">{card.title}</h3>
                      <p className="city-view__info-text">{card.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Travel Ideas */}
          {travelIdeas.length > 0 && (
            <section className="city-view__section">
              <h2 className="city-view__heading">
                {fromSections.travelIdeas?.heading || `Things to do in ${toCityName} with a cab`}
              </h2>
              <p className="city-view__paragraph">{fromSections.travelIdeas?.subText}</p>

              <div className="city-view__travel-grid">
                {travelIdeas.map((card, idx) => (
                  <div key={idx} className="city-view__travel-card">
                    <h3 className="city-view__travel-title">{card.title}</h3>
                    <p className="city-view__travel-text">{card.text}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Route Guide / Distance & Time */}
          <section className="city-view__section city-view__section--highlighted">
            <h2 className="city-view__heading">Route Guide: {fromCityName} to {toCityName}</h2>
            <p className="city-view__paragraph">
              <strong>Distance:</strong> {distance ? `${Math.ceil(distance)} km` : "Calculated at booking"}
            </p>
            <p className="city-view__paragraph">
              <strong>Travel Time:</strong> The journey from {fromCityName} to {toCityName} typically takes around{" "}
              {popularRoutes.find((r) => r.linkText?.includes(toCityName))?.distanceAndTime?.split(", ")[1] ||
                "4-5 hours"}, depending on traffic and road conditions.
            </p>
            <p className="city-view__paragraph">
              <strong>Best Time to Travel:</strong> Early mornings (6 AM - 9 AM) and late evenings (6 PM - 9 PM) are
              ideal for avoiding traffic. Weekend travel may have higher demand.
            </p>
            <p className="city-view__paragraph">
              <strong>Recommended Stops:</strong> Consider a short break at a midpoint location for refreshments and to
              stretch your legs during the journey.
            </p>
          </section>

          {/* FAQ */}
          {faqItems.length > 0 && (
            <section className="city-view__section">
              <h2 className="city-view__heading">
                {fromSections.faq?.heading || `FAQs for ${fromCityName} to ${toCityName} Cab Booking`}
              </h2>
              <p className="city-view__paragraph">{fromSections.faq?.subText}</p>

              <div className="city-view__faq-grid">
                {faqItems.map((faq, index) => (
                  <div key={index} className="city-view__faq-card">
                    <h3 className="city-view__faq-question">{faq.q}</h3>
                    <p className="city-view__faq-answer">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Why Choose */}
          {whyChooseFeatures.length > 0 && (
            <section className="city-view__section city-view__section--features">
              <h2 className="city-view__heading">
                {fromSections.whyChoose?.heading || `Why Choose Pulpit for ${fromCityName} to ${toCityName}`}
              </h2>

              <div className="city-view__features-list">
                {whyChooseFeatures.map((f, idx) => (
                  <div key={idx} className="city-view__feature">
                    <span className="city-view__feature-icon">
                      <Icon
                        path={ICON_MAP[f.iconKey] || mdiCheckCircle}
                        size={24}
                        color="#ffffff"
                      />
                    </span>
                    <div>
                      <h4 className="city-view__feature-title">{f.title}</h4>
                      <p className="city-view__feature-text">{f.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="city-view__section city-view__section--cta">
            <div className="city-view__cta-box">
              <h2 className="city-view__cta-title">
                {fromSections.cta?.heading || `Ready to Book Your Cab from ${fromCityName} to ${toCityName}?`}
              </h2>
              <p className="city-view__cta-text">
                {fromSections.cta?.subText ||
                  `For reliable one-way outstation cabs from ${fromCityName} to ${toCityName}, book now!`}
              </p>

              <div className="city-view__cta-buttons">
                <button
                  className="city-view__cta-button city-view__cta-button--primary"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                >
                  Select Cab
                </button>
              </div>

            </div>
          </section>

          {/* Popular Routes Sections */}
          {popularCitiesInState.length > 0 && (
            <>
              {/* Top Cab Routes */}
              <section className="city-view__section">
                <div className="bg-white rounded-[10px] shadow-sm px-[12px] py-[12px] md:px-[44px] md:py-[32px] border">
                  <h2 className="text-[16px] font-black text-[#1B1B1B] mb-6">
                    Top Cab Routes from {fromCityName}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {popularCitiesInState.slice(0, 21).map((city, index) => {
                      const routeUrl = `/routes/${slugify(fromCity)}-to-${slugify(city)}-cab`
                      const routeText = `${fromCityName} to ${city} Cab`
                      return (
                        <Link
                          key={index}
                          to={routeUrl}
                          title={routeText}
                          className="block px-3 py-2 text-[15px] font-medium text-[#1B1B1B] bg-gray-50 hover:bg-[#2DB8B3] hover:text-white rounded-md border border-gray-200 hover:border-[#2DB8B3] transition-all duration-200 truncate"
                        >
                          {routeText}
                        </Link>
                      )
                    })}
                  </div>
                </div>
              </section>

              {/* Cab from Top Cities */}
              <section className="city-view__section">
                <div className="bg-white rounded-[10px] shadow-sm px-[12px] py-[12px] md:px-[44px] md:py-[32px] border">
                  <h2 className="text-[16px] font-black text-[#1B1B1B] mb-6">
                  Cab Routes to {toCityName} from Popular Cities
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {popularCitiesInState.slice(0, 21).map((city, index) => {
                      // Mix: from selected city to current toCity
                      const routeUrl = `/routes/${slugify(city)}-to-${slugify(toCity)}-cab`
                      const routeText = `${city} to ${toCityName} Cab`
                      return (
                        <Link
                          key={index}
                          to={routeUrl}
                          title={routeText}
                          className="block px-3 py-2 text-[15px] font-medium text-[#1B1B1B] bg-gray-50 hover:bg-[#2DB8B3] hover:text-white rounded-md border border-gray-200 hover:border-[#2DB8B3] transition-all duration-200 truncate"
                        >
                          {routeText}
                        </Link>
                      )
                    })}
                  </div>
                </div>
              </section>

              {/* Top Airport Cab Routes */}
              <section className="city-view__section">
                <div className="bg-white rounded-[10px] shadow-sm px-[12px] py-[12px] md:px-[44px] md:py-[32px] border">
                  <h2 className="text-[16px] font-black text-[#1B1B1B] mb-6">
                    Top Airport Cab Routes from {fromCityName}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {popularCitiesInState
                      .filter(city => city !== fromCityName) // Exclude fromCity itself
                      .slice(0, 21)
                      .map((city, index) => {
                        const routeUrl = `/routes/${slugify(fromCity)}-to-${slugify(city)}-cab`
                        const routeText = `${fromCityName} Airport to ${city} Cab`
                        return (
                          <Link
                            key={index}
                            to={routeUrl}
                            title={routeText}
                            className="block px-3 py-2 text-[15px] font-medium text-[#1B1B1B] bg-gray-50 hover:bg-[#2DB8B3] hover:text-white rounded-md border border-gray-200 hover:border-[#2DB8B3] transition-all duration-200 truncate"
                          >
                            {routeText}
                          </Link>
                        )
                      })}
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default CityPairView
